"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Statistics = void 0;
const store_1 = __importDefault(require("@app/store"));
const i18n_1 = require("@app/util/i18n");
const chart_js_1 = require("chart.js");
const chartjs_plugin_datalabels_1 = __importDefault(require("chartjs-plugin-datalabels"));
const react_chartjs_2_1 = require("react-chartjs-2");
const react_redux_1 = require("react-redux");
require("./index.scss");
const graphql_1 = require("@app/gql-types/graphql");
const util_1 = require("@app/util");
const maybe_unwrap_1 = require("@app/util/apollo/maybe-unwrap");
const useFetch_1 = require("@app/util/apollo/useFetch");
const format_hours_1 = require("@app/util/format-hours");
const _ui_1 = require("@ui");
const moment_1 = __importDefault(require("moment"));
const react_1 = __importStar(require("react"));
const redux_1 = require("redux");
const is_loading_1 = __importDefault(require("@app/ac/is-loading"));
const performance_query_1 = require("./performance.query");
const mapStateToProps = (state) => {
    const { calendar, workspaces, workspaceId, positions } = state;
    return {
        calendar,
        // @ts-expect-error
        workspace: workspaces.find((ws) => ws.id === workspaceId),
        positions: positions.filter((p) => !p.archived),
    };
};
// interface DispatchProps {
// ...
// }
const mapDispatchToProps = (dispatch) => {
    return (0, redux_1.bindActionCreators)({
    // ...
    }, dispatch);
};
chart_js_1.Chart.register(chart_js_1.LinearScale, chart_js_1.LineElement, chart_js_1.PointElement, chart_js_1.Tooltip, chartjs_plugin_datalabels_1.default);
// Chart.js draws its labels onto a canvas, which inherits nothing from CSS — without this
// it keeps its own default stack ('Helvetica Neue', Helvetica, Arial) while the rest of the
// app is in Roboto.
chart_js_1.Chart.defaults.font.family = "'Roboto', 'Arial', 'Helvetica', sans-serif";
// Distinct colors for the (dashed) performance-metric lines, cycled by order.
const METRIC_COLORS = [
    '#E5484D',
    '#F76808',
    '#8E4EC6',
    '#0091FF',
    '#30A46C',
    '#D6409F',
];
// Format a metric value for its datalabel by the column's displayAs (the chart
// plots the raw number; this only affects the printed label).
const formatMetric = (value, displayAs) => {
    switch (displayAs) {
        case graphql_1.DisplayAs.Duration:
            return (0, format_hours_1.formatHours)(value);
        case graphql_1.DisplayAs.Percentage:
            return `${util_1.numberUtil.round2decimals(value)} %`;
        default:
            return String(util_1.numberUtil.round2decimals(value));
    }
};
const StatsComponent = ({ shiftsInPeriod, timeOffsInPeriod, dateStringsInPeriod, labelWidthRem, style, calendar, workspace, positions, }) => {
    const storedDisplayedDatasets = JSON.parse(window.localStorage.getItem('ds-calendar-statistics-displayed') ?? '[]');
    const [displayedDatasets, setDisplayedDatasets] = (0, react_1.useState)(storedDisplayedDatasets || ['allShifts']);
    // ---- Performance metrics (columns flagged displayedInCalendar) ----------
    // The displayedInCalendar flag lives on the workspace's performance columns,
    // not on the aggregated data, so we load the (cheap) column list to know what
    // to offer in the dropdown. The heavy performanceData is fetched lazily, only
    // once the user actually toggles a metric on (see below).
    const workspaceId = workspace?.id;
    const { data: perfColumnsData } = (0, useFetch_1.useFetch)(performance_query_1.WORKSPACE_PERFORMANCE_COLUMNS_QUERY, {
        variables: { workspaceId: workspaceId ?? '' },
        skipInitial: !(0, util_1.isValue)(workspaceId),
    });
    const calendarColumns = (perfColumnsData?.workspace?.performanceColumns ?? []).filter((c) => c.displayedInCalendar);
    const selectedPerfColumns = calendarColumns.filter((c) => displayedDatasets.includes(c.id));
    // day/week → hourly granularity; month → daily (one bar per day).
    const perfRowUnit = calendar?.view === 'month' ? graphql_1.PerformanceRowUnit.Day : graphql_1.PerformanceRowUnit.Hour;
    // Period bounds from the visible days (sorted; dateStringsInPeriod is
    // 'YYYY-MM-DD').
    const sortedDayStrings = [...new Set(dateStringsInPeriod)].sort();
    const periodStart = sortedDayStrings.length
        ? (0, moment_1.default)(sortedDayStrings[0]).startOf('day').toISOString()
        : '';
    const periodEnd = sortedDayStrings.length
        ? (0, moment_1.default)(sortedDayStrings[sortedDayStrings.length - 1])
            .endOf('day')
            .toISOString()
        : '';
    const { data: perfData, loading: perfLoading } = (0, useFetch_1.useFetch)(performance_query_1.CALENDAR_PERFORMANCE_DATA_QUERY, {
        variables: {
            workspaceId: workspaceId ?? '',
            period: { start: periodStart, end: periodEnd },
            rowUnit: perfRowUnit,
        },
        // Don't fetch the aggregation until at least one metric is toggled on.
        skipInitial: !(0, util_1.isValue)(workspaceId) ||
            selectedPerfColumns.length === 0 ||
            periodStart === '',
    });
    const perfResult = perfData?.workspace?.performanceData;
    // Surface the fetch in the calendar's existing loading notice (no separate
    // spinner here — the calendar already lists what's loading above the grid).
    (0, react_1.useEffect)(() => {
        store_1.default.dispatch((0, is_loading_1.default)('load-performance-data', !perfLoading));
        return () => {
            store_1.default.dispatch((0, is_loading_1.default)('load-performance-data', true));
        };
    }, [perfLoading]);
    const datasets = [];
    const datasetLabels = [];
    // compute shifts and mins on per-quarter-hour basis
    const getDayStats = (d) => {
        const hourBarsEvents = [];
        let hourBarsCount = 1;
        if (calendar?.view !== 'month') {
            hourBarsCount = 24 * 4; // 24 * 4 because we render one bar for each quarter hour
        }
        for (let i = 0; i < hourBarsCount; i++) {
            hourBarsEvents[i] = { allShifts: new Set() };
            positions.forEach((p) => {
                hourBarsEvents[i][p.id] = new Set();
            });
            workspace?.unavailabilityCategories.forEach((c) => {
                hourBarsEvents[i][c.id] = new Set();
            });
        }
        // count shifts and minutes in the day
        const shiftsInDay = shiftsInPeriod.filter((s) => (0, moment_1.default)(d).isSame(s.period.start, 'day'));
        const timeOffsInDay = timeOffsInPeriod.filter((s) => (0, moment_1.default)(d).isSame(s.period.start, 'day'));
        // loop over all the events in the day
        const events = shiftsInDay.map((s) => Object.assign({}, s, { type: 'shift' }));
        timeOffsInDay
            .map((t) => Object.assign({}, t, { type: 'timeOff' }))
            .forEach((t) => {
            events.push(t);
        });
        events.forEach((evt) => {
            // in 'month' view, we only have single-element 'hourBarsShifts', and we ignore pauses, so we can skip most of the logic
            // console.log(evt)
            if (calendar?.view === 'month') {
                if (evt.type === 'shift') {
                    hourBarsEvents[0].allShifts.add(evt.userId);
                    if ((0, util_1.isUndefined)(evt.positionId))
                        return;
                    if (Object.keys(hourBarsEvents[0]).includes(evt.positionId))
                        hourBarsEvents[0][evt.positionId].add(evt.userId);
                }
                else if (evt.type === 'timeOff') {
                    if ((0, util_1.isUndefined)(evt.categoryId))
                        return;
                    if (Object.keys(hourBarsEvents[0]).includes(evt.categoryId))
                        hourBarsEvents[0][evt.categoryId].add(evt.userId);
                }
                return;
            }
            const hrStart = parseInt((0, moment_1.default)(evt.period.start).format('H')) * 4 +
                Math.floor(parseInt((0, moment_1.default)(evt.period.start).format('m')) / 15);
            let hrEnd = parseInt((0, moment_1.default)(evt.period.end).format('H')) * 4 +
                Math.floor(parseInt((0, moment_1.default)(evt.period.end).format('m')) / 15);
            if (hrEnd <= hrStart)
                hrEnd += hourBarsCount; // treat events ending in the next day
            // loop over all the quarter-hours intersected by the shift
            // and increase their count
            for (let j = hrStart; j < hrEnd; j++) {
                if (j < hourBarsCount) {
                    if (evt.type === 'shift') {
                        hourBarsEvents[j].allShifts.add(evt.userId);
                        if ((0, util_1.isUndefined)(evt.positionId))
                            return;
                        if (Object.keys(hourBarsEvents[j]).includes(evt.positionId))
                            hourBarsEvents[j][evt.positionId].add(evt.userId);
                    }
                    else if (evt.type === 'timeOff') {
                        if ((0, util_1.isUndefined)(evt.categoryId))
                            return;
                        if (Object.keys(hourBarsEvents[j]).includes(evt.categoryId))
                            hourBarsEvents[j][evt.categoryId].add(evt.userId);
                    }
                }
            }
            // loop over all the quarter-hours intersected by the all the shift's pauses
            // and decrease their count
            if (evt.type === 'shift') {
                ;
                (evt.pauses || []).forEach((p) => {
                    if ((0, util_1.isUndefined)(evt.positionId))
                        return;
                    const pStartMoment = (0, moment_1.default)(evt.period.start).add(p.start, 'minutes');
                    const pStart = parseInt(pStartMoment.format('H')) * 4 +
                        Math.floor(parseInt(pStartMoment.format('m')) / 15);
                    const pEndMoment = pStartMoment.clone().add(p.duration, 'minutes');
                    let pEnd = parseInt(pEndMoment.format('H')) * 4 +
                        Math.floor(parseInt(pEndMoment.format('m')) / 15);
                    if (pEnd <= pStart)
                        pEnd += hourBarsCount;
                    for (let j = pStart; j < pEnd; j++) {
                        if (j < hourBarsCount) {
                            hourBarsEvents[j].allShifts.delete(evt.userId);
                            if (Object.keys(hourBarsEvents[j]).includes(evt.positionId))
                                hourBarsEvents[j][evt.positionId].delete(evt.userId);
                        }
                    }
                });
            }
        });
        // @ts-expect-error Type '{ allShifts: Set<string>; }[]' is not assignable to type '{ [key: string]: number | undefined; allShifts: number; }[]'.
        return hourBarsEvents.map((hb) => {
            const ret = Object.assign({}, hb);
            for (const k in ret) {
                ret[k] = ret[k].size;
            }
            return ret;
        });
    };
    const uniqueDayStrings = dateStringsInPeriod.filter(function (item, pos) {
        return dateStringsInPeriod.indexOf(item) === pos;
    });
    const statsByDay = uniqueDayStrings.map((d) => {
        return { day: d, stats: getDayStats(d) };
    });
    statsByDay.forEach((d) => {
        d.stats.forEach((v, j) => {
            for (const key in v) {
                const pos = key === 'allShifts' ? null : positions.find((p) => p.id === key);
                const cat = workspace?.unavailabilityCategories?.find((c) => c.id === key);
                const label = key === 'allShifts' ? (0, i18n_1.t)('PEOPLE') : pos ? pos.name : cat?.title;
                if (!datasets.some((ds) => ds.label === label)) {
                    const color = key === 'allShifts'
                        ? '#0045FF' // allShifts color
                        : pos?.color
                            ? '#' + pos.color // position's color
                            : '#495063'; // timeOff color
                    datasets.push({
                        label: label ?? '',
                        data: [],
                        borderColor: color,
                        backgroundColor: color,
                        borderWidth: key === 'allShifts' ? 2.75 : 1.75,
                        hidden: !displayedDatasets.includes(key),
                    });
                }
                const idx = datasets.findIndex((ds) => ds.label === label);
                datasets[idx].data.push(v[key]);
            }
            const m = (0, moment_1.default)(d.day, 'YYYY-MM-DD');
            datasetLabels.push((calendar?.view === 'day' ? '' : m.format('D.M. ')) +
                (calendar?.view !== 'month'
                    ? m
                        .clone()
                        .add(j * 15, 'minutes')
                        .format('HH:mm')
                    : ''));
        });
    });
    // Append the selected performance-metric lines. Their data array must match
    // datasetLabels' length/order exactly: one entry per (day, quarter-hour bar)
    // in day/week, or one per day in month — same layout the shift loop produced.
    if (selectedPerfColumns.length > 0 && (0, util_1.isDefined)(perfResult)) {
        const barsPerDay = calendar?.view === 'month' ? 1 : 24 * 4;
        // Index perf rows by day(+hour) on period.start.
        // - month (day granularity): the BE encodes the day as UTC midnight
        //   (e.g. 2026-06-23T00:00:00Z), so take the UTC date — it lands on that
        //   calendar day regardless of browser timezone.
        // - day/week (hour granularity): bucket by *local* day+hour, matching how
        //   the shift bars above are placed, so the line aligns with them.
        const perfRowByKey = new Map();
        perfResult.rows.forEach((row) => {
            if (!(0, util_1.isValue)(row.period?.start))
                return;
            if (calendar?.view === 'month') {
                perfRowByKey.set(moment_1.default.utc(row.period?.start).format('YYYY-MM-DD'), row);
            }
            else {
                const rm = (0, moment_1.default)(row.period?.start);
                perfRowByKey.set(`${rm.format('YYYY-MM-DD')}|${rm.format('H')}`, row);
            }
        });
        selectedPerfColumns.forEach((col, ci) => {
            // Match by column id → resolves the right source tag even under
            // per-workspace source overrides.
            const meta = perfResult.columns.find((c) => c.id === col.id);
            if (!(0, util_1.isValue)(meta))
                return;
            const lineData = [];
            uniqueDayStrings.forEach((day) => {
                for (let j = 0; j < barsPerDay; j++) {
                    const key = calendar?.view === 'month'
                        ? day
                        : `${day}|${Math.floor(j / 4)}`;
                    const cell = perfRowByKey
                        .get(key)
                        ?.cells.find((c) => c.columnTag === meta.tag);
                    lineData.push((0, util_1.isValue)(cell?.value) ? (cell?.value ?? null) : null);
                }
            });
            const color = METRIC_COLORS[ci % METRIC_COLORS.length];
            datasets.push({
                label: col.name,
                data: lineData,
                borderColor: color,
                backgroundColor: color,
                borderWidth: 2,
                hidden: false,
                borderDash: [6, 4],
                metricDisplayAs: col.displayAs,
            });
        });
    }
    // The x axis is linear over [0, datasetLabels.length], so every bar gets an equally wide
    // band that lines up with the calendar's day columns. Each value sits in the middle of
    // its band (stepped: 'middle' switches exactly on the band borders), and the first/last
    // values are repeated at 0 and at the end so the line reaches both edges.
    const barsCount = datasetLabels.length;
    const barsPerCol = calendar?.view === 'month' ? 1 : calendar?.view === 'week' ? 24 * 4 : 4;
    const data = {
        labels: [0]
            .concat(datasetLabels.map((_, i) => i + 0.5))
            .concat([barsCount]),
        datasets: datasets.map((ds) => Object.assign({}, ds, {
            data: [ds.data[0]].concat(ds.data).concat([ds.data[ds.data.length - 1]]),
        })),
    };
    const optsDatasets = [
        {
            label: (0, i18n_1.t)('PEOPLE'),
            value: 'allShifts',
        },
    ]
        .concat(positions.map((p) => {
        return {
            label: p.name ?? '',
            value: p.id,
            groupTitle: (0, i18n_1.t)('POSITIONS'),
        };
    }))
        .concat(workspace?.unavailabilityCategories.map((c) => {
        return {
            label: c.title,
            value: c.id,
            groupTitle: (0, i18n_1.t)('TIMEOFFS'),
        };
    }) ?? [])
        .concat(calendarColumns.map((c) => {
        return {
            label: c.name,
            value: c.id,
            groupTitle: (0, i18n_1.t)('ORG_SP_WORKSPACES_PERFORMANCE'),
        };
    }));
    return (react_1.default.createElement("div", { className: 'ds-c-statistics is-view-' + (calendar?.view ?? ''), style: style },
        react_1.default.createElement("div", { className: 'ds-c-row-label', style: {
                width: labelWidthRem.toString() + 'rem',
                minWidth: labelWidthRem.toString() + 'rem',
                maxWidth: labelWidthRem.toString() + 'rem',
                paddingLeft: '0.75rem',
            } },
            react_1.default.createElement(_ui_1.Flex, { direction: _ui_1.Flex.DIRECTION.COLUMN, justify: _ui_1.Flex.POSITION.CENTER, stretch: true, flex: 'auto' },
                react_1.default.createElement(_ui_1.Dropdown, { label: null, searchable: true, disableSort: true, size: _ui_1.Dropdown.SIZES.FULL_WIDTH, type: _ui_1.Dropdown.TYPES.VARIABLE, style: _ui_1.Dropdown.STYLES.BORDERLESS, options: optsDatasets, value: optsDatasets.filter((opt) => displayedDatasets.includes(opt.value)), onChange: (selected) => {
                        setDisplayedDatasets(selected.map((s) => s.value));
                        window.localStorage.setItem('ds-calendar-statistics-displayed', JSON.stringify(selected.map((s) => s.value)));
                    }, cb: undefined }))),
        react_1.default.createElement("div", { style: {
                width: '100%',
                overflow: 'hidden',
            } },
            react_1.default.createElement(react_chartjs_2_1.Line, { options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: {
                        mode: 'index',
                        intersect: false,
                    },
                    animation: false,
                    layout: {
                        padding: 0,
                    },
                    locale: workspace?.locale,
                    elements: {
                        line: {
                            stepped: 'middle',
                            spanGaps: true,
                        },
                        point: {
                            radius: 0,
                            hoverRadius: 0,
                        },
                    },
                    scales: {
                        x: {
                            type: 'linear',
                            min: 0,
                            max: barsCount,
                            border: {
                                display: false,
                            },
                            ticks: {
                                display: false,
                                // one grid line per calendar column (day, or hour in the day view)
                                stepSize: barsPerCol,
                            },
                            grid: {
                                tickLength: 0,
                                color: '#efefef',
                            },
                        },
                        y: {
                            border: {
                                display: false,
                            },
                            grid: {
                                display: false,
                                tickLength: 0,
                            },
                            ticks: {
                                display: false,
                            },
                        },
                    },
                    plugins: {
                        // Legend is registered globally by the manager assistant chat; the
                        // dropdown on the left already serves as this chart's legend.
                        legend: {
                            display: false,
                        },
                        tooltip: {
                            itemSort: function (a, b) {
                                // @ts-expect-error 'b.raw' is of type 'unknown'.
                                return b.raw > a.raw ? 1 : -1;
                            },
                            // Format performance-metric values per their displayAs (same
                            // rule as the datalabels / performance table); other datasets
                            // keep the default "label: value".
                            callbacks: {
                                // x values are band positions, so map the point back to its bar's label
                                // (the edge points repeat the first/last bar).
                                title: function (items) {
                                    const barIdx = Math.min(Math.max((items[0]?.dataIndex ?? 0) - 1, 0), barsCount - 1);
                                    return datasetLabels[barIdx] ?? '';
                                },
                                label: function (context) {
                                    const displayAs = context.dataset?.metricDisplayAs;
                                    const value = context.parsed.y;
                                    return `${context.dataset.label ?? ''}: ${(0, util_1.isValue)(displayAs) ? formatMetric(value, displayAs) : value}`;
                                },
                            },
                            enabled: false,
                            external: function (context) {
                                // Tooltip Element
                                let tooltipEl = document.getElementById('chartjs-tooltip');
                                // Create element on first render
                                if (!tooltipEl) {
                                    tooltipEl = document.createElement('div');
                                    tooltipEl.id = 'chartjs-tooltip';
                                    tooltipEl.classList.add('detect-zone'); // 'detect-zone' class and 'content' class inside it are here, so we just use Tooltip component's CSS
                                    // Display-only tooltip: without this, the div keeps blocking hover
                                    // and clicks at its last position even while hidden (opacity 0).
                                    tooltipEl.style.pointerEvents = 'none';
                                    tooltipEl.innerHTML =
                                        '<div class="content"><div style="padding: 0.4rem 0.8rem"><table></table></div></div>';
                                    document.body.appendChild(tooltipEl);
                                }
                                // Hide if no tooltip
                                const tooltipModel = context.tooltip;
                                if (tooltipModel.opacity === 0) {
                                    tooltipEl.style.opacity = '0';
                                    return;
                                }
                                function getBody(bodyItem) {
                                    return bodyItem.lines;
                                }
                                // Set Text
                                if (tooltipModel.body) {
                                    const titleLines = tooltipModel.title || [];
                                    const bodyLines = tooltipModel.body.map(getBody);
                                    let innerHtml = '<thead>';
                                    titleLines.forEach(function (title) {
                                        innerHtml += '<tr><th>' + title + '</th></tr>';
                                    });
                                    innerHtml += '</thead><tbody>';
                                    bodyLines.forEach(function (body, i) {
                                        const colors = tooltipModel.labelColors[i];
                                        /* eslint-disable-next-line @typescript-eslint/restrict-plus-operands, @typescript-eslint/no-base-to-string */
                                        innerHtml +=
                                            '<tr><td><div style="display: flex; align-items: center;"><div style="width: 0.5rem; height: 0.5rem; border-radius: 0.25rem; margin: 0 0.3rem 0.15rem 0; background-color: ' +
                                                colors.backgroundColor +
                                                '"></div>' +
                                                body +
                                                '</div></td></tr>';
                                    });
                                    innerHtml += '</tbody>';
                                    const tableRoot = (0, maybe_unwrap_1.unwrapMaybe)(tooltipEl.querySelector('table'));
                                    if ((0, util_1.isDefined)(tableRoot)) {
                                        tableRoot.innerHTML = innerHtml;
                                    }
                                }
                                // Display, position and styles
                                const position = context.chart.canvas.getBoundingClientRect();
                                tooltipEl.style.opacity = '1';
                                tooltipEl.style.position = 'absolute';
                                tooltipEl.style.left =
                                    tooltipModel.x + tooltipModel.width <
                                        tooltipModel.chart.width - 200
                                        ? (position.left +
                                            window.pageXOffset +
                                            tooltipModel.caretX).toString() + 'px'
                                        : (position.left +
                                            window.pageXOffset +
                                            tooltipModel.caretX -
                                            tooltipModel.width).toString() + 'px';
                                tooltipEl.style.top =
                                    (position.top +
                                        window.pageYOffset +
                                        tooltipModel.chart.height -
                                        5).toString() + 'px';
                            },
                        },
                        datalabels: {
                            display: true,
                            align: calendar?.view === 'week' ? 'left' : 'top',
                            offset: calendar?.view === 'week' ? 2 : -3,
                            formatter: function (value, context) {
                                if (value === 0)
                                    return null;
                                // the edge points only stretch the line to the chart's borders
                                const dataIndex = context?.dataIndex ?? 0;
                                if (dataIndex === 0 || dataIndex === barsCount + 1)
                                    return null;
                                const displayAs = context?.dataset?.metricDisplayAs;
                                const fmt = (v) => (0, util_1.isValue)(displayAs) ? formatMetric(v, displayAs) : v;
                                if (calendar?.view === 'day' || calendar?.view === 'week') {
                                    if (Array.isArray(context?.dataset?.data) &&
                                        context.dataset.data.length) {
                                        const prevValue = dataIndex > 1 ? context.dataset.data[dataIndex - 1] : undefined;
                                        if (value !== prevValue) {
                                            return fmt(value);
                                        }
                                        else {
                                            return null;
                                        }
                                    }
                                }
                                return fmt(value);
                            },
                            color: function (context) {
                                const dataset = context.chart.data.datasets[context.datasetIndex];
                                return dataset.borderColor;
                            },
                        },
                    },
                }, data: data }))));
};
exports.Statistics = (0, react_redux_1.connect)(
// @ts-expect-error No overload matches this call.
mapStateToProps, mapDispatchToProps)(react_1.default.memo(StatsComponent, (prevProps, nextProps) => {
    // this function decides when the component should re-render
    return (util_1.miscUtil.safeStringify(prevProps.dateStringsInPeriod) ===
        util_1.miscUtil.safeStringify(nextProps.dateStringsInPeriod) &&
        util_1.miscUtil.safeStringify(prevProps.shiftsInPeriod) ===
            util_1.miscUtil.safeStringify(nextProps.shiftsInPeriod) &&
        util_1.miscUtil.safeStringify(prevProps.timeOffsInPeriod) ===
            util_1.miscUtil.safeStringify(nextProps.timeOffsInPeriod) &&
        util_1.miscUtil.safeStringify(prevProps.style) ===
            util_1.miscUtil.safeStringify(nextProps.style));
}));
